import React from 'react';
import { Box, CircularProgress, Typography, Fade } from '@mui/material';
import { useAppSelector } from 'src/hooks/redux';

interface LoadingScreenProps {
    message?: string;
    fullScreen?: boolean;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ 
    message = 'Loading Telix Lab...', 
    fullScreen = true 
}) => {
    const { theme } = useAppSelector(state => state.ui);
    const isDark = theme === 'dark';

    const content = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                minHeight: fullScreen ? '100vh' : '400px',
                width: '100%',
                background: isDark
                    ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                position: fullScreen ? 'fixed' : 'relative',
                top: 0,
                left: 0,
                zIndex: fullScreen ? 9999 : 'auto',
            }}
        >
            <Fade in={true} timeout={1000}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 3,
                    }}
                >
                    {/* Logo */}
                    <Box
                        component="img"
                        src={isDark ? '/telixia-logo-color-736px.png' : '/telixia-logo-color-736px.png'}
                        alt="Telix Lab Logo"
                        sx={{
                            height: { xs: 120, sm: 150, md: 180 },
                            width: 'auto',
                            maxWidth: '90%',
                            objectFit: 'contain',
                            filter: isDark ? 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.3))' : 'none',
                            animation: 'pulse 2s ease-in-out infinite',
                            '@keyframes pulse': {
                                '0%, 100%': {
                                    opacity: 1,
                                    transform: 'scale(1)',
                                },
                                '50%': {
                                    opacity: 0.8,
                                    transform: 'scale(1.05)',
                                },
                            },
                        }}
                    />

                    {/* Loading Spinner */}
                    <CircularProgress
                        size={48}
                        thickness={4}
                        sx={{
                            color: isDark ? 'primary.light' : 'white',
                            '& .MuiCircularProgress-circle': {
                                strokeLinecap: 'round',
                            },
                        }}
                    />

                    {/* Loading Message */}
                    <Typography
                        variant="h6"
                        sx={{
                            color: isDark ? 'primary.light' : 'white',
                            fontWeight: 500,
                            textAlign: 'center',
                            px: 2,
                            textShadow: isDark ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                    >
                        {message}
                    </Typography>

                    {/* Version/Subtitle */}
                    <Typography
                        variant="caption"
                        sx={{
                            color: isDark ? 'text.secondary' : 'rgba(255, 255, 255, 0.8)',
                            textAlign: 'center',
                            mt: -1,
                        }}
                    >
                        Virtual Lab Platform for Robotics & AI
                    </Typography>
                </Box>
            </Fade>
        </Box>
    );

    return content;
};

export default LoadingScreen;

